import {
  useState, useEffect, useCallback,
} from 'react';

const LISTS_TTL_MS = 30000;
const listeners = new Set();
function notifyListeners() {
  listeners.forEach((fn) => fn());
}

const moduleCache = {
  applications: [],
  invites: [],
  collaborationStatuses: {},
  fetchedAt: 0,
};

let inFlight = null;

const API = () => process.env.REACT_APP_API_URL || '';

export const resolveAcceptanceId = (data, item) => {
  if (!data) return null;
  if (data.acceptance_id != null) return data.acceptance_id;
  if (data.acceptanceId != null) return data.acceptanceId;
  if (data.id != null && String(data.id) !== String(item.id)) {
    return data.id;
  }
  return null;
};

export const getWorkQueueStatusKey = (type, id) => `${type}_${id}`;

export const normalizeStatusRecord = (data, item, type) => {
  if (!item) {
    return null;
  }
  if (!data) {
    return {
      id: item.id,
      detailKey: getWorkQueueStatusKey(type, item.id),
      sourceItemId: item.id,
      sourceType: type,
      acceptanceId: null,
      acceptanceStatus: null,
      status: 'active',
      address_verified: false,
      addressVerified: false,
      contractUrl: null,
      productShipped: false,
    };
  }
  const acceptanceId = resolveAcceptanceId(data, item);
  return {
    ...data,
    id: item.id,
    detailKey: getWorkQueueStatusKey(type, item.id),
    sourceItemId: item.id,
    sourceType: type,
    acceptanceId: acceptanceId ?? null,
    acceptanceStatus: data.status,
    addressVerified: data.address_verified || false,
    contractUrl: data.contract_url || null,
    productShipped: data.product_shipped || false,
  };
};

export const fetchStatusForItem = async (item, token, type) => {
  const t = type || 'invite';
  try {
    const endpoint = t === 'invite'
      ? `${API()}/api/acceptances/by-source/invite/${item.id}`
      : `${API()}/api/acceptances/by-source/application/${item.id}`;

    const response = await fetch(endpoint, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return normalizeStatusRecord(data, item, t);
    }
    if (response.status === 404) {
      const fallbackEndpoint = t === 'invite'
        ? `${API()}/api/campaigns/invites/${item.id}`
        : `${API()}/api/campaign/application/${item.id}`;
      const fallbackResponse = await fetch(fallbackEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (fallbackResponse.ok) {
        const fallbackData = await fallbackResponse.json();
        return normalizeStatusRecord(fallbackData, item, t);
      }
    }
  } catch (e) {
    // use stub below
  }
  return normalizeStatusRecord({ status: 'active' }, item, t);
};

export const fetchStatusesForItems = async (items, token) => {
  if (!items.length) {
    return [];
  }

  try {
    const response = await fetch(`${API()}/api/acceptances/by-sources`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        items: items.map(({ item, type }) => ({
          sourceType: type,
          sourceId: item.id,
        })),
      }),
    });

    if (response.ok) {
      const { results = [] } = await response.json();
      const bySource = new Map(results.map((row) => [
        getWorkQueueStatusKey(row.sourceType, row.sourceId),
        row.acceptance,
      ]));

      const rows = await Promise.all(items.map(async ({ item, type }) => {
        const data = bySource.get(getWorkQueueStatusKey(type, item.id));
        if (data) {
          return normalizeStatusRecord(data, item, type);
        }
        return fetchStatusForItem(item, token, type);
      }));

      return rows;
    }
  } catch {
    // Fall back to individual lookups below.
  }

  return Promise.all(items.map(({ item, type }) => fetchStatusForItem(item, token, type)));
};

export async function fetchWorkQueueData(force) {
  const now = Date.now();
  if (!force && moduleCache.fetchedAt && (now - moduleCache.fetchedAt) < LISTS_TTL_MS) {
    return {
      applications: moduleCache.applications,
      invites: moduleCache.invites,
      collaborationStatuses: { ...moduleCache.collaborationStatuses },
    };
  }
  if (inFlight) {
    return inFlight;
  }
  inFlight = (async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return { applications: [], invites: [], collaborationStatuses: {} };
    }

    const [applicationsRes, invitesRes] = await Promise.all([
      fetch(`${API()}/api/campaign/applications`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }),
      fetch(`${API()}/api/campaigns/invites/sent`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      }),
    ]);

    const applications = applicationsRes.ok ? await applicationsRes.json() : [];
    const invites = invitesRes.ok ? await invitesRes.json() : [];

    const stLower = (s) => String(s || '').toLowerCase();
    const acceptApps = (applications || []).filter((a) => {
      const s = stLower(a?.status);
      return s === 'accepted' || s === 'approved' || s === 'active' || s === 'completed' || s === 'closed';
    });
    const acceptInvs = (invites || []).filter((i) => {
      const s = stLower(i?.status);
      return s === 'accepted' || s === 'completed' || s === 'closed';
    });

    const byId = {};
    const statusItems = [
      ...acceptApps.map((item) => ({ item, type: 'application' })),
      ...acceptInvs.map((item) => ({ item, type: 'invite' })),
    ];
    const rows = await fetchStatusesForItems(statusItems, token);
    rows.forEach((row) => { if (row) byId[row.detailKey] = row; });

    moduleCache.applications = Array.isArray(applications) ? applications : [];
    moduleCache.invites = Array.isArray(invites) ? invites : [];
    moduleCache.collaborationStatuses = byId;
    moduleCache.fetchedAt = Date.now();

    const out = {
      applications: moduleCache.applications,
      invites: moduleCache.invites,
      collaborationStatuses: { ...moduleCache.collaborationStatuses },
    };
    return out;
  })().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

/**
 * Application + sent invites + acceptance/collaboration details for brand work-queue surfaces.
 * Shared module cache and single-flight fetches to avoid duplicate API load across Layout + pages.
 */
/** Resets module cache (for tests and rare logout flows). */
export function resetBrandWorkQueueCache() {
  inFlight = null;
  moduleCache.applications = [];
  moduleCache.invites = [];
  moduleCache.collaborationStatuses = {};
  moduleCache.fetchedAt = 0;
}

/** Marks list cache stale so the next fetch returns fresh invites/applications. */
export function invalidateBrandWorkQueueCache() {
  moduleCache.fetchedAt = 0;
}

/** Forces a work-queue refetch and notifies subscribed dashboard surfaces. */
export async function refreshBrandWorkQueue() {
  invalidateBrandWorkQueueCache();
  await fetchWorkQueueData(true);
  notifyListeners();
}

export default function useBrandCollaborationWorkQueue({ enabled = true } = {}) {
  const [applications, setApplications] = useState(() => moduleCache.applications);
  const [invites, setInvites] = useState(() => moduleCache.invites);
  const [collaborationStatuses, setCollaborationStatuses] = useState(() => (
    { ...moduleCache.collaborationStatuses }
  ));
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const syncFromCache = useCallback(() => {
    setApplications(moduleCache.applications);
    setInvites(moduleCache.invites);
    setCollaborationStatuses({ ...moduleCache.collaborationStatuses });
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    listeners.add(syncFromCache);
    return () => listeners.delete(syncFromCache);
  }, [enabled, syncFromCache]);

  const runFetch = useCallback(async (force) => {
    if (!localStorage.getItem('token')) {
      setError(null);
      setLoading(false);
      return;
    }
    try {
      if (!moduleCache.fetchedAt || force) {
        setLoading(true);
      }
      setError(null);
      const data = await fetchWorkQueueData(!!force);
      setApplications(data.applications);
      setInvites(data.invites);
      setCollaborationStatuses({ ...data.collaborationStatuses });
      notifyListeners();
    } catch (e) {
      setError((e && e.message) || 'Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (moduleCache.fetchedAt) {
        syncFromCache();
        if (!cancelled) setLoading(false);
        if (!cancelled) {
          const t = Date.now();
          if (t - moduleCache.fetchedAt >= LISTS_TTL_MS) {
            runFetch(true);
          }
        }
        return;
      }
      if (!cancelled) await runFetch(true);
    })();
    return () => { cancelled = true; };
  }, [enabled, runFetch, syncFromCache]);

  const refetch = useCallback(({ force = true } = {}) => {
    if (force) {
      invalidateBrandWorkQueueCache();
    }
    return runFetch(force);
  }, [runFetch]);

  const invalidate = useCallback(() => {
    invalidateBrandWorkQueueCache();
  }, []);

  return {
    applications,
    invites,
    collaborationStatuses,
    loading,
    error,
    refetch,
    invalidate,
  };
}
